import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import client, { formatApiError } from "@/lib/api";

const SECTORS = ["Automotriz", "Industrial", "Naval", "Suministros"];
const EMPTY = { title: "", description: "", sector: "Industrial", image: "", result: "", order: 0 };
const inputCls = "bg-gema-surface border-white/15 text-white rounded-none focus-visible:ring-gema-red";

export default function CaseManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => client.get("/admin/cases").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: items.length + 1 }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ title: it.title, description: it.description, sector: it.sector, image: it.image, result: it.result, order: it.order }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) };
      if (editing) await client.put(`/admin/cases/${editing.id}`, payload);
      else await client.post("/admin/cases", payload);
      toast.success(editing ? "Caso actualizado" : "Caso creado");
      setOpen(false);
      load();
    } catch (err) {
      toast.error("Error", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este caso?")) return;
    await client.delete(`/admin/cases/${id}`);
    toast.success("Caso eliminado");
    load();
  };

  return (
    <div data-testid="case-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold uppercase text-white text-lg">Casos de Éxito ({items.length})</h2>
        <button onClick={openNew} data-testid="case-new-btn" className="btn-primary py-2.5 px-5"><Plus className="h-4 w-4" /> Nuevo</button>
      </div>

      <div className="border border-white/10 divide-y divide-white/10">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-4 p-4 hover:bg-gema-surface transition-colors">
            <img src={it.image} alt="" className="h-12 w-16 object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{it.title}</p>
              <p className="text-white/40 text-xs mt-1">{it.sector} · Orden {it.order}</p>
            </div>
            <button onClick={() => openEdit(it)} data-testid={`case-edit-${it.id}`} className="p-2 text-white/60 hover:text-gema-red transition-colors"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(it.id)} data-testid={`case-delete-${it.id}`} className="p-2 text-white/60 hover:text-gema-red transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 text-white/40 text-sm">Sin casos aún.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-gema-ink border border-white/10 text-white rounded-none max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading uppercase">{editing ? "Editar" : "Nuevo"} caso</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="case-form">
            <div><Label className="text-white/70 text-xs uppercase">Título</Label><Input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70 text-xs uppercase">Sector</Label>
                <Select value={form.sector} onValueChange={(v) => set("sector", v)}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gema-ink border-white/15 text-white">{SECTORS.map((s) => <SelectItem key={s} value={s} className="focus:bg-gema-red focus:text-white">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-white/70 text-xs uppercase">Orden</Label><Input type="number" className={inputCls} value={form.order} onChange={(e) => set("order", e.target.value)} /></div>
            </div>
            <div><Label className="text-white/70 text-xs uppercase">Imagen (URL)</Label><Input className={inputCls} value={form.image} onChange={(e) => set("image", e.target.value)} required /></div>
            <div><Label className="text-white/70 text-xs uppercase">Descripción</Label><Textarea rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} required /></div>
            <div><Label className="text-white/70 text-xs uppercase">Resultado</Label><Input className={inputCls} value={form.result} onChange={(e) => set("result", e.target.value)} /></div>
            <button type="submit" disabled={saving} className="btn-primary w-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
